import { CssMixBlendMode } from '@nivo/core';
import { DynamicFilters, FilterNames } from 'common/types/filters';
import { Owner } from 'common/types/common';
import { Folder } from './folders';
import { ChartHeaderNames } from 'modules/charts/components/GrowthPerformance/types';

export enum MOEStatus {
  NOT_PERMITTED,
  DISABLED,
  ENABLED,
}

export enum ChartFields {
  COUNTRY = 'country',
  WAVE = 'wave',
  BRAND = 'brand',
  SEGMENT = 'segment',
  KPI = 'kpi',
  MEAN = 'mean',
  MARGIN_OF_ERROR = 'margin_of_error',
}

export enum ChartViews {
  LINE = 'line',
  BAR = 'bar',
  HORIZONTAL = 'horizontal',
}

export enum ChartNumbersType {
  PERCENTAGE = 'percentage',
  VALUES = 'values',
  BOTH = 'both',
  TOTAL = 'total',
  BOTH_TOTAL = 'both_total',
}

export enum ChartLayout {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum ChartType {
  FUNNEL = 'funnel',
  COMPARISON = 'comparison',
  HEATMAP = 'heatmap',
  RADAR = 'radar',
  GAUGE = 'gauge',
  MARKET_SIZE = 'market_size',
  GROWTH_PERFORMANCE = 'growth_performance',
  RANKING = 'ranking',
  BRAND_PERCEPTION = 'brand_perception',
  MARKET_FUNNEL = 'market_funnel',
  BRAND_FUNNEL = 'brand_funnel',
  MATRIX = 'matrix',
}

export type ChartFilterId = number | string;

export interface SelectedSort {
  key: string | null;
  type: SortType;
}

export interface RawChart {
  data: RawChartData[];
  chart: RawChartSchema;
  /**
   * Branching rows are keyed by parent option identifier in `kpi` and matched to a chart slice by
   * `wave`, `brand`, `segment`, and `country` before total/rebased calculations are applied.
   */
  branching_data: RawChartData[];
}

export interface ChartConfiguration {
  first_dimension: FilterNames;
  second_dimension: FilterNames;
  y_label: ChartFields;
  x_label: ChartFields;
  chart_value_representation: ChartNumbersType;
  second_dimension_items?: number[];
  change_over_time?: boolean;
}

export interface RawChartSchema {
  uuid: string | undefined;
  name: string;
  description: string | null;
  chart_type: ChartType;
  configuration: ChartConfiguration;
  wave_ids: number[];
  brand_ids: number[];
  /** Brand Funnel only: the user-selected brand. `brand_ids` then holds the backend-computed comparison set. */
  focus_brand_id?: number;
  segment_ids: number[];
  country_ids: number[];
  kpi_option_identifiers: string[];
  current_view: ChartViews | null;
  current_sort?: SelectedSort;
  display_reference_average?: boolean;
  dynamic_waves: DynamicFilters | null;
  margin_of_error_status?: MOEStatus;
  study_uuid: string;
  updated_at: string;
  owner: Owner | null;
  tags: Array<string>;
  cadence: CadencePeriod;
  folders?: Folder[];
}

export interface RawChartData {
  country: number;
  /**
   * A cadence entry id for cadence charts and a raw wave id otherwise. It has always carried the
   * label string at runtime -- `normalizeRowsWave` declares the same union in its own generic --
   * and Growth Performance indexes rows by it directly.
   */
  wave: number | string;
  brand: number;
  segment: number;
  kpi: string;
  mean: number;
  margin_of_error?: number;
  segment_population_weight?: number;
  high?: number;
  low?: number;
  unavailable?: boolean;
  positive?: number;
  neutral?: number;
  negative?: number;
  question?: string;
  branched_from?: string | string[] | null;
  /**
   * Custom KPI rows only. The row's own values combine the custom KPI's options as they are ("Branched");
   * `total` combines them after rebasing each option to a share of the whole segment ("Total", "Value").
   * null when an option couldn't be rebased. The frontend can't rebase a custom KPI itself, as its options
   * are combined before they reach the chart: see rebasedValueMath.ts.
   */
  total?: RebasedTotal | null;
}

export interface RebasedTotal {
  mean: number;
  margin_of_error: number;
  high: number;
  low: number;
}

export interface ChartDataPointTooltipItem {
  label: string;
  color?: string;
  value: number;
  numericValue: boolean;
  moeValue?: number;
  unavailable: boolean;
  labelColor?: unknown;
  evolutionValue?: number;
  highlight?: boolean;
  populationValue: string;
  moePopulationValue?: string;
  totalValue?: number;
  totalMoeValue?: number;
  rebasedFromLabels?: string[];
}

export interface ChartDataPointTooltip {
  title: string;
  subtitle?: string;
  items: ChartDataPointTooltipItem[];
  segments?: string[];
  kpis?: string[];
  groupBy?: string;
}

export interface FunnelTooltiop {
  kpiLabel: string;
  value: number;
  refValue: number | null;
  margin: number | null;
  absolute: number | null;
}

export interface ChartValueFilterLabels {
  brand: string;
  kpi: string;
  country: string;
  wave: string;
  segment: string;
}
export interface ChartDataPoint {
  count: number;
  tooltip: ChartDataPointTooltip;
  delta: number | null;
  deltaPopulation: string;
  value_0: number;
  value_0_raw: number;
  value_0_numeric: boolean;
  value_0_height: number;
  value_0_max_height: number;
  value_0_bar_fill: number;
  value_0_bar_label: string | null;
  value_0_funnel_fill: number;
  value_0_moe: number;
  value_0_moe_height: number;
  value_0_moe_raw: number;
  value_0_unavailable: boolean;
  value_0_label: string;
  value_0_label_value: string | Date;
  value_0_color: string;
  value_0_label_color: string;
  /** A raw wave id, or a cadence entry id on a cadence chart -- see `RawChartData.wave`. */
  value_0_wave_id: number | string;
  value_0_wave_label: string;
  funnel_0_tooltip: FunnelTooltiop | null;
  value_0_secondDimension: string | number;
  value_0_firstDimension: string | number;
  value_0_disabled?: boolean;
  value_0_populationNumberValue: string;
  value_0_populationNumberRaw: number;
  value_0_moePopulationRaw?: number;
  value_0_segmentPopulationNumber: number;
  value_0_segmentSize: number;
  value_0_filterLabels: ChartValueFilterLabels;
  value_0_totalValueRaw: number;
  value_0_totalValue: number;
  value_0_totalMoeRaw: number;
  value_0_totalMoeValue: number;
  /** The value couldn't be rebased (an ancestor's data is missing), so Total and Value modes fall back to the branched share. */
  value_0_notRebased?: boolean;
  aidedIndex: number;
  referenceValue: number | null;
  color: string;
  disabled: boolean;
  groupId: string;
  secondDimension?: string;
  groupLabel: string;
}

export interface GeneratedChartData {
  data: ChartDataPoint[];
  keys: string[];
  indexBy: FilterNames;
  secondDimension: FilterNames;
  chartType: ChartType;
  groups: number;
  maxValue: number;
  // lowest value (or MoE bound when MoE is on) as a fraction, never above 0; see getMinValue
  minValue: number;
  maxMoE?: number;
  maxSegmentPopulationSize: number;
  missingBranchingRows?: string[];
}

export type ChartDataKey = keyof ChartDataPoint;

export interface ReferenceBrand {
  id: number;
  name: string;
}

export interface RawReferenceAverageData {
  country: number;
  wave: number;
  segment: number;
  kpi: string;
  mean: number;
  reference_brands: ReferenceBrand[];
}

export enum SortType {
  ASC = 'ascending',
  DESC = 'descending',
  DEFAULT = 'default',
}

export interface SortOption {
  key: string | null | ConesSortingKeys;
  type: SortType | null;
}

export interface CurrentChartState {
  id: string | null | undefined;
  chartType: ChartType | undefined;
}

export interface RadarChartData {
  kpi: number;
  [key: string]: number;
}

/**
 * Note: wave, kpi, brand, segments are multivalue fields and are handled by checkboxes
 * and checkboxes are values
 */

export interface ChartFormData {
  uuid?: string;
  name: string;
  description: string | null;
  chart_type: ChartType;
  country: Array<number>;
  wave: Array<number>;
  kpi: Array<string>;
  brand: Array<number>;
  segment: Array<number>;
  firstDimension: FilterNames | null;
  secondDimension: FilterNames | null; // this cannot be always computed, will be set before submit
  current_view: ChartViews | null;
  chartNumbersType: ChartNumbersType;
  display_reference_average?: boolean;
  margin_of_error_status?: MOEStatus;
  dynamic_waves: DynamicFilters | null;
  study_uuid: string;
  owner: Owner | null;
  secondDimensionItems?: Array<number>; // This is needed for Matrix chart, to store selected items in filter
  cadence: CadencePeriod;
  folders?: Folder[];
}

export enum CadencePeriod {
  WEEKLY = 'weeks',
  MONTHLY = 'months',
  QUARTERLY = 'quarters',
  BI_ANNUALLY = 'half_years',
  ANNUALLY = 'years',
}

export interface GaugeLegends {
  name: string;
  color: string;
}

export interface TableChartHeader {
  key: null | string;
  title: string | null;
  align?: 'left' | 'center' | 'right';
  width?: number;
  headerName?: ChartHeaderNames | null;
}

export type TBarTooltip = {
  title: string;
  items: ChartDataPointTooltipItem[];
  segments: string[];
  kpis: string[];
  groupBy: string;
  subtitle?: string;
};

interface Legend {
  anchor: 'bottom' | 'left' | 'right' | 'top' | 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left' | 'center';
  direction: 'column' | 'row';
  effects: Array<{
    on: 'hover';
    style: {
      itemTextColor: string;
    };
  }>;
  itemHeight: number;
  itemTextColor: string;
  itemWidth: number;
  symbolShape: 'circle' | 'diamond' | 'square' | 'triangle';
  symbolSize: number;
  translateX: number;
  translateY: number;
}

export interface Params {
  blendMode: CssMixBlendMode | undefined;
  borderColor: string;
  borderWidth: number;
  dotBorderWidth: number;
  dotColor: string | number;
  dotSize: number;
  fillOpacity: number;
  gridShape: 'circular' | 'linear';
  indexBy: string;
  labelOffset: number;
  legends: Array<Legend>;
  margin: { top: number; right: number; bottom: number; left: number };
  motionConfig: string;
}

// This is for Market Funnel chart, to define the sorting options for cones
export enum ConesSortingKeys {
  INDEX = 'index',
  POPULATION = 'population',
  AWARENESS = 'Awareness-From 1 month ago',
  CONSIDERATION = 'Consideration-From 1 month ago',
}
